// Landing Page — Diag SCOP Prévention
// Design moderne SaaS avec sections hero, features, HALTES, casques, attestation, FAQ

import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Button } from '../components/ui/button'
import { Card, CardContent } from '../components/ui/card'
import { Badge } from '../components/ui/badge'
import { Separator } from '../components/ui/separator'
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '../components/ui/accordion'
import { LogoPreventionScop, LogoOppbtp, LogoScop } from '../components/layout'
import { cn } from '../lib/utils'
import { trackEvent } from '../lib/analytics'
import {
  Shield,
  Clock,
  BarChart3,
  Target,
  Award,
  ArrowRight,
  CheckCircle2,
  ClipboardCheck,
  Zap,
  Building2,
} from 'lucide-react'

export default function LandingPage() {
  const navigate = useNavigate()
  const goToDashboard = () => {
    trackEvent('demarrer le diagnostic')
    navigate('/dashboard')
  }
  const [activeEval, setActiveEval] = useState<'haltes' | 'management'>('haltes')

  return (
    <div className="min-h-screen bg-background">
      {/* ===== HERO (logo intégré) ===== */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary/5 via-white to-primary/3 pt-10 pb-20 sm:pt-14 sm:pb-28 lg:pt-16 lg:pb-32">
        {/* Blobs décoratifs */}
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-primary/3 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Logo */}
          <div className="mb-12 sm:mb-16">
            <LogoPreventionScop className="h-10 sm:h-12 w-auto" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Texte */}
            <div className="animate-fade-in-up">


              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-foreground leading-tight">
                Évaluez et améliorez la{' '}
                <span className="text-primary">sécurité</span> de votre entreprise
              </h1>

              <p className="mt-6 text-lg sm:text-xl text-muted-foreground max-w-xl leading-relaxed">
                Le diagnostic SCOP Prévention est un outil qui vous permet d'évaluer
                vos pratiques en matière de prévention des risques, d'identifier vos axes
                d'amélioration et d'obtenir une attestation de niveau.
              </p>

              <div className="mt-8">
                <Button size="lg" onClick={goToDashboard} className="text-base px-8 py-6">
                  Démarrer le diagnostic
                  <ArrowRight className="size-5 ml-2" />
                </Button>
              </div>
            </div>

            {/* Visuel casques */}
            <div className="flex justify-center lg:justify-end">
              <div className="relative">
                <Card className="p-8 bg-white/80 backdrop-blur-sm shadow-xl border-primary/10">
                  <div className="text-center">
                    <p className="text-sm font-medium text-muted-foreground mb-4">Votre niveau de prévention</p>
                    <div className="flex items-end justify-center gap-2 sm:gap-3 mb-6">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <img
                          key={i}
                          src="/images/casque-actif.svg"
                          alt=""
                          className="w-10 h-10 sm:w-14 sm:h-14"
                        />
                      ))}
                    </div>
                    <div className="text-2xl font-bold text-foreground">5 casques sur 5</div>
                    <p className="text-sm text-primary font-medium mt-1">Excellence en prévention</p>
                    <div className="mt-4 h-2 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-primary rounded-full" style={{ width: '100%' }} />
                    </div>
                  </div>
                </Card>
                {/* Badge doré flottant */}
                <div className="absolute -top-4 -right-4 w-16 h-16 animate-bounce-once">
                  <img src="/images/medaille-dore.svg" alt="Badge doré" className="w-full h-full drop-shadow-lg" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== 3. CHIFFRES CLÉS ===== */}
      <section className="border-y border-border bg-muted/30 py-10 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 text-center">
            {STATS.map((stat) => (
              <div key={stat.label} className="flex flex-col items-center gap-2">
                <stat.icon className="size-6 text-primary" />
                <div className="text-3xl sm:text-4xl font-bold text-foreground">{stat.value}</div>
                <div className="text-sm text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 4. FONCTIONNALITÉS ===== */}
      <section className="py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <Badge variant="outline" className="mb-4">Fonctionnalités</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground">
              Un outil complet pour votre prévention
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              De l'évaluation à l'attestation, tout pour piloter la sécurité de votre entreprise.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {FEATURES.map((feature) => (
              <Card key={feature.title} className="p-0 hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-8">
                  <div className={cn('w-12 h-12 rounded-xl flex items-center justify-center mb-6', feature.color)}>
                    <feature.icon className="size-6" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">{feature.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 5. COMMENT ÇA MARCHE ===== */}
      <section className="py-20 sm:py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <Badge variant="outline" className="mb-4">Comment ça marche</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground">
              4 étapes pour évaluer votre entreprise
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 relative">
            {/* Ligne de connexion desktop */}
            <div className="hidden lg:block absolute top-7 left-[12.5%] right-[12.5%] h-0.5 bg-border" />

            {STEPS.map((step) => (
              <div key={step.num} className="relative text-center">
                <div className="relative z-10 w-14 h-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-6 text-lg font-bold shadow-md">
                  {step.num}
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">{step.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 6. ÉVALUATION — HALTES / MANAGEMENT (slide) ===== */}
      <section className="py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Badge variant="outline" className="mb-4">Référentiel PREMS</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Une évaluation complète en 11 axes
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Le diagnostic s'appuie sur le référentiel PREMS de l'OPPBTP.
              Deux questionnaires couvrent les risques terrain et le management de la prévention.
            </p>
          </div>

          {/* Toggle HALTES / Management */}
          <div className="flex justify-center mb-12">
            <div className="inline-flex rounded-xl bg-muted p-1.5 gap-1">
              <button
                onClick={() => setActiveEval('haltes')}
                className={cn(
                  'px-6 py-2.5 rounded-lg text-sm font-semibold transition-all',
                  activeEval === 'haltes'
                    ? 'bg-primary text-primary-foreground shadow-md'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <Shield className="size-4 inline-block mr-2 -mt-0.5" />
                HALTES - Risques terrain
              </button>
              <button
                onClick={() => setActiveEval('management')}
                className={cn(
                  'px-6 py-2.5 rounded-lg text-sm font-semibold transition-all',
                  activeEval === 'management'
                    ? 'bg-primary text-primary-foreground shadow-md'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <ClipboardCheck className="size-4 inline-block mr-2 -mt-0.5" />
                Management - ORGA + BILAN
              </button>
            </div>
          </div>

          {/* Contenu slide */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Texte + liste axes */}
            <div>
              {activeEval === 'haltes' ? (
                <>
                  <h3 className="text-2xl font-bold text-foreground mb-2">Méthode HALTES</h3>
                  <p className="text-sm text-muted-foreground mb-2">5 axes d'évaluation des risques terrain</p>
                  <p className="text-base text-muted-foreground mb-8 leading-relaxed">
                    La méthode HALTES est le référentiel de l'OPPBTP pour évaluer la prévention
                    des risques sur les chantiers. Chaque axe couvre un domaine clé de la sécurité.
                  </p>
                  <div className="space-y-4">
                    {HALTES_AXES.map((axe) => (
                      <div key={axe.key} className="flex items-start gap-4 p-4 rounded-xl hover:bg-muted/50 transition-colors">
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 text-primary-foreground font-bold text-sm bg-primary">
                          {axe.key}
                        </div>
                        <div>
                          <h4 className="font-semibold text-foreground">{axe.label}</h4>
                          <p className="text-sm text-muted-foreground mt-0.5">{axe.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <h3 className="text-2xl font-bold text-foreground mb-2">Management de la prévention</h3>
                  <p className="text-sm text-muted-foreground mb-2">6 axes : organisation (ORGA) et suivi (BILAN)</p>
                  <p className="text-base text-muted-foreground mb-8 leading-relaxed">
                    Le questionnaire management évalue l'organisation de la prévention (ORGA)
                    et le suivi des actions sur le terrain (BILAN).
                  </p>
                  <div className="space-y-4">
                    {MANAGEMENT_AXES.map((axe, i) => (
                      <div key={axe.key} className="flex items-start gap-4 p-4 rounded-xl hover:bg-muted/50 transition-colors">
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 text-primary-foreground font-bold text-sm bg-primary">
                          {axe.short}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-foreground">{axe.label}</h4>
                            <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded-full font-medium">{i < 4 ? 'ORGA' : 'BILAN'}</span>
                          </div>
                          <p className="text-sm text-muted-foreground mt-0.5">{axe.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Radar SVG */}
            <div className="flex justify-center">
              <div className="relative w-80 h-80 sm:w-96 sm:h-96">
                {activeEval === 'haltes' ? (
                  <svg viewBox="0 0 400 400" className="w-full h-full">
                    {/* Grilles pentagone */}
                    <polygon points="200,40 370,160 330,340 70,340 30,160" fill="none" stroke="var(--border)" strokeWidth="1" />
                    <polygon points="200,100 310,180 285,300 115,300 90,180" fill="none" stroke="var(--border)" strokeWidth="0.7" />
                    <polygon points="200,160 250,200 240,260 160,260 150,200" fill="none" stroke="var(--border)" strokeWidth="0.7" />
                    {/* Axes */}
                    <line x1="200" y1="200" x2="200" y2="40" stroke="var(--border)" strokeWidth="0.5" />
                    <line x1="200" y1="200" x2="370" y2="160" stroke="var(--border)" strokeWidth="0.5" />
                    <line x1="200" y1="200" x2="330" y2="340" stroke="var(--border)" strokeWidth="0.5" />
                    <line x1="200" y1="200" x2="70" y2="340" stroke="var(--border)" strokeWidth="0.5" />
                    <line x1="200" y1="200" x2="30" y2="160" stroke="var(--border)" strokeWidth="0.5" />
                    {/* Zone remplie */}
                    <polygon points="200,70 350,170 310,325 90,325 55,165" fill="var(--primary)" fillOpacity="0.12" stroke="var(--primary)" strokeWidth="2.5" />
                    {/* Points */}
                    <circle cx="200" cy="70" r="6" fill="var(--primary)" />
                    <circle cx="350" cy="170" r="6" fill="var(--primary)" />
                    <circle cx="310" cy="325" r="6" fill="var(--primary)" />
                    <circle cx="90" cy="325" r="6" fill="var(--primary)" />
                    <circle cx="55" cy="165" r="6" fill="var(--primary)" />
                    {/* Labels */}
                    <text x="200" y="25" textAnchor="middle" fill="var(--foreground)" fontSize="14" fontWeight="700">H</text>
                    <text x="390" y="165" textAnchor="start" fill="var(--foreground)" fontSize="14" fontWeight="700">A</text>
                    <text x="340" y="365" textAnchor="start" fill="var(--foreground)" fontSize="14" fontWeight="700">LT</text>
                    <text x="55" y="365" textAnchor="end" fill="var(--foreground)" fontSize="14" fontWeight="700">E</text>
                    <text x="15" y="165" textAnchor="end" fill="var(--foreground)" fontSize="14" fontWeight="700">S</text>
                  </svg>
                ) : (
                  <svg viewBox="0 0 400 400" className="w-full h-full">
                    {/* Grilles hexagone */}
                    {[1, 0.66, 0.33].map((ratio) => {
                      const r = 160 * ratio
                      const pts = Array.from({ length: 6 }, (_, i) => {
                        const angle = (Math.PI * 2 * i) / 6 - Math.PI / 2
                        return `${200 + r * Math.cos(angle)},${200 + r * Math.sin(angle)}`
                      }).join(' ')
                      return <polygon key={ratio} points={pts} fill="none" stroke="var(--border)" strokeWidth={ratio === 1 ? '1' : '0.7'} />
                    })}
                    {/* Axes */}
                    {Array.from({ length: 6 }, (_, i) => {
                      const angle = (Math.PI * 2 * i) / 6 - Math.PI / 2
                      return <line key={i} x1="200" y1="200" x2={200 + 160 * Math.cos(angle)} y2={200 + 160 * Math.sin(angle)} stroke="var(--border)" strokeWidth="0.5" />
                    })}
                    {/* Zone remplie */}
                    {(() => {
                      const ratios = [0.85, 0.7, 0.75, 0.8, 0.65, 0.9]
                      const pts = ratios.map((r, i) => {
                        const angle = (Math.PI * 2 * i) / 6 - Math.PI / 2
                        return `${200 + 160 * r * Math.cos(angle)},${200 + 160 * r * Math.sin(angle)}`
                      }).join(' ')
                      return <polygon points={pts} fill="var(--primary)" fillOpacity="0.12" stroke="var(--primary)" strokeWidth="2.5" />
                    })()}
                    {/* Points */}
                    {[0.85, 0.7, 0.75, 0.8, 0.65, 0.9].map((r, i) => {
                      const angle = (Math.PI * 2 * i) / 6 - Math.PI / 2
                      return <circle key={i} cx={200 + 160 * r * Math.cos(angle)} cy={200 + 160 * r * Math.sin(angle)} r="6" fill="var(--primary)" />
                    })}
                    {/* Labels */}
                    {MANAGEMENT_AXES.map((axe, i) => {
                      const angle = (Math.PI * 2 * i) / 6 - Math.PI / 2
                      const lx = 200 + 190 * Math.cos(angle)
                      const ly = 200 + 190 * Math.sin(angle)
                      const anchor = lx < 195 ? 'end' : lx > 205 ? 'start' : 'middle'
                      return <text key={axe.key} x={lx} y={ly} textAnchor={anchor} dy="0.35em" fill="var(--foreground)" fontSize="13" fontWeight="700">{axe.short}</text>
                    })}
                  </svg>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== 7. CASQUES 1-5 ===== */}
      <section className="py-20 sm:py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <Badge variant="outline" className="mb-4">Système de notation</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground">
              De 1 à 5 casques : votre niveau de maturité
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              Chaque casque représente un palier de progression dans votre démarche de prévention.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {CASQUE_LEVELS.map((item) => (
              <Card
                key={item.level}
                className={cn(
                  'p-0 text-center transition-all hover:shadow-md',
                  item.level === 5 && 'border-primary/30 bg-primary/5'
                )}
              >
                <CardContent className="p-6">
                  <div className="flex justify-center gap-1 mb-4">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <img
                        key={i}
                        src={i < item.level ? '/images/casque-actif.svg' : '/images/casque-desactive.svg'}
                        alt=""
                        className={cn('w-6 h-6 sm:w-8 sm:h-8', i >= item.level && 'opacity-30')}
                      />
                    ))}
                  </div>
                  <div className="text-2xl font-bold text-foreground mb-1">{item.level}</div>
                  <div className="font-semibold text-primary text-sm mb-1">{item.label}</div>
                  <p className="text-xs text-muted-foreground">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Badge doré */}
          <div className="mt-8 flex justify-center">
            <div className="inline-flex items-center gap-4 px-6 py-4 rounded-2xl border border-dashed border-amber-300 bg-amber-50/50">
              <img src="/images/medaille-dore.svg" alt="Badge doré" className="w-10 h-10" />
              <div className="text-left">
                <div className="font-semibold text-amber-800 text-sm">Badge doré MASE / ISO 45001</div>
                <p className="text-xs text-amber-600">
                  5 casques + certification = médaille dorée et reconnaissance officielle.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== 8. ATTESTATION ===== */}
      <section className="py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Mockup attestation */}
            <div className="flex justify-center">
              <Card className="w-full max-w-md shadow-xl border-primary/10 p-0 overflow-hidden">
                <div className="bg-primary/5 px-6 py-4 border-b border-primary/10 flex items-center justify-between">
                  <div className="text-xs font-medium text-primary uppercase tracking-wide">Attestation officielle</div>
                  <img src="/images/medaille-dore.svg" alt="" className="w-6 h-6" />
                </div>
                <CardContent className="p-6">
                  <div className="text-center mb-6">
                    <LogoPreventionScop className="h-6 w-auto mx-auto mb-3" />
                    <h3 className="text-lg font-bold text-foreground">Diag SCOP Prévention</h3>
                    <p className="text-sm text-muted-foreground mt-1">Délivrée par l'OPPBTP</p>
                  </div>

                  <div className="flex justify-center gap-2 mb-6">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <img key={i} src="/images/casque-actif.svg" alt="" className="w-8 h-8" />
                    ))}
                    <img src="/images/medaille-dore.svg" alt="" className="w-8 h-8 ml-1" />
                  </div>

                  <div className="space-y-3 bg-muted/50 rounded-lg p-4 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Entreprise</span>
                      <span className="font-medium text-foreground">BE - Bâtiment Exemple</span>
                    </div>
                    <Separator />
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Métier</span>
                      <span className="font-medium text-foreground">Maçonnerie</span>
                    </div>
                    <Separator />
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Validité</span>
                      <span className="font-medium text-foreground">2 ans</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Texte attestation */}
            <div>
              <Badge variant="outline" className="mb-4">
                <Award className="size-3.5 mr-1" />
                Attestation
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
                Une reconnaissance officielle de votre engagement
              </h2>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                À l'issue du diagnostic, générez votre attestation officielle délivrée par l'OPPBTP.
                Elle témoigne de votre engagement dans la démarche de prévention.
              </p>

              <div className="space-y-4">
                {ATTESTATION_POINTS.map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="size-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-foreground">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== 9. FAQ ===== */}
      <section className="py-20 sm:py-24 bg-muted/30">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">FAQ</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground">
              Questions fréquentes
            </h2>
          </div>

          <Accordion type="single" collapsible className="w-full">
            {FAQ_ITEMS.map((faq, index) => (
              <AccordionItem key={index} value={`faq-${index}`}>
                <AccordionTrigger className="text-base font-medium text-foreground py-5 text-left">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ===== 10. CTA FINAL ===== */}
      <section className="py-20 sm:py-24 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center gap-2 mb-6">
            {Array.from({ length: 5 }).map((_, i) => (
              <img key={i} src="/images/casque-actif.svg" alt="" className="w-10 h-10 sm:w-12 sm:h-12" />
            ))}
          </div>

          <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
            Prêt à évaluer votre entreprise ?
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            Rejoignez les SCOP du BTP qui s'engagent dans la démarche prévention.
            Diagnostic gratuit, résultats immédiats.
          </p>

          <Button size="lg" onClick={goToDashboard} className="text-base px-10 py-6 shadow-lg">
            Démarrer le diagnostic
            <ArrowRight className="size-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* ===== 11. FOOTER ===== */}
      <footer className="border-t border-border bg-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex items-center gap-6">
              <a href="https://www.scopbtp.org/" target="_blank" rel="noopener noreferrer">
                <LogoScop className="h-10 w-auto" />
              </a>
              <Separator orientation="vertical" className="h-8" />
              <a href="https://www.oppbtp.com/" target="_blank" rel="noopener noreferrer">
                <LogoOppbtp className="h-5 w-auto" />
              </a>
            </div>

            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <Link to="/mentions-legales" className="hover:text-foreground transition-colors">Mentions légales</Link>
              <Link to="/confidentialite" className="hover:text-foreground transition-colors">Confidentialité</Link>
              <Link to="/contact" className="hover:text-foreground transition-colors">Contact</Link>
            </div>
          </div>

          <Separator className="my-8" />

          <div className="text-center text-xs text-muted-foreground">
            Diag SCOP Prévention © 2026
          </div>
        </div>
      </footer>
    </div>
  )
}

// ─── Données statiques ────────────────────────────────────────

const STATS = [
  { value: '22', label: 'métiers du BTP', icon: Building2 },
  { value: '44', label: 'questions ciblées', icon: ClipboardCheck },
  { value: '50+', label: 'solutions OPPBTP', icon: Zap },
  { value: '15 min', label: "pour s'évaluer", icon: Clock },
]

const FEATURES = [
  {
    icon: BarChart3,
    title: 'Diagnostic personnalisé',
    description:
      "Deux questionnaires adaptés à votre métier : risques terrain (HALTES) et management de la prévention. Score détaillé par axe avec radar visuel.",
    color: 'text-primary bg-primary/10',
  },
  {
    icon: Target,
    title: "Plan d'actions guidé",
    description:
      "Plus de 50 solutions OPPBTP classées par thème, difficulté et coût. Construisez votre plan d'actions et suivez sa mise en œuvre.",
    color: 'text-blue-600 bg-blue-50',
  },
  {
    icon: Award,
    title: 'Attestation officielle',
    description:
      "Obtenez votre attestation de 1 à 5 casques. Intégration MASE ou ISO 45001 pour le badge doré avec médaille.",
    color: 'text-amber-600 bg-amber-50',
  },
]

const STEPS = [
  {
    num: 1,
    title: 'Identification',
    description: "Connectez-vous via votre compte PréventionBTP et sélectionnez vos métiers (jusqu'à 3).",
  },
  {
    num: 2,
    title: 'Diagnostic',
    description: '44 questions réparties en 2 questionnaires : risques terrain et management de la prévention.',
  },
  {
    num: 3,
    title: 'Résultats',
    description: 'Score détaillé par axe, radar de performance, niveau de casques attribué de 1 à 5.',
  },
  {
    num: 4,
    title: "Plan d'actions",
    description: "Explorez le catalogue de solutions OPPBTP, construisez votre plan et obtenez votre attestation.",
  },
]

const HALTES_AXES = [
  { key: 'H', label: 'Hygiène', description: 'Conditions sanitaires, vestiaires, eau potable, propreté du chantier.' },
  { key: 'A', label: 'Accès', description: 'Accès au chantier, circulation, signalisation, balisage des zones.' },
  { key: 'LT', label: 'Livraisons / Tâches', description: 'Organisation des livraisons, réception des matériaux, tâches à risques sur chantier.' },
  { key: 'E', label: 'Énergies', description: 'Risques électriques, gaz, matières dangereuses, équipements sous pression.' },
  { key: 'S', label: 'Secours', description: "Premiers secours, évacuation, formation SST, matériel d'urgence." },
]

const MANAGEMENT_AXES = [
  { key: 'orientations', short: 'O', label: 'Orientations', description: 'Politique prévention, engagement de la direction, objectifs et moyens.' },
  { key: 'roles', short: 'R', label: 'Rôles et missions', description: 'Organisation des responsabilités, désignation des référents, compétences.' },
  { key: 'gestion', short: 'G', label: 'Gestion des moyens', description: 'Ressources humaines, matérielles et financières dédiées à la prévention.' },
  { key: 'animation', short: 'A', label: 'Animation', description: 'Communication prévention, réunions, sensibilisation, participation des salariés.' },
  { key: 'bilan', short: 'B', label: 'Bilans chantier', description: "Retour d'expérience, analyse des incidents, évaluation des pratiques terrain." },
  { key: 'audits', short: 'A', label: 'Audits', description: 'Audits internes, contrôles et suivi des actions correctives.' },
]

const CASQUE_LEVELS = [
  { level: 1, label: 'Engagé', description: 'Début du parcours prévention' },
  { level: 2, label: 'Impliqué', description: 'Premiers pas significatifs' },
  { level: 3, label: 'Performant', description: 'Bonne maîtrise des risques' },
  { level: 4, label: 'Expert', description: 'Engagement exemplaire' },
  { level: 5, label: 'Excellence', description: 'Modèle pour le secteur' },
]

const ATTESTATION_POINTS = [
  'Attestation PDF personnalisée avec votre score et vos métiers',
  'Validité de 1 an (2 ans pour le niveau 5 casques)',
  'Intégration MASE ou ISO 45001 pour le badge doré',
  'Suivi multi-diagnostic pour mesurer votre progression',
]

const FAQ_ITEMS = [
  {
    question: "À quoi sert l'outil Diag SCOP Prévention ?",
    answer:
      "Cet outil d'auto-évaluation vous permet d'évaluer vos pratiques de prévention des risques professionnels via deux questionnaires complémentaires : HALTES (5 axes risques terrain) et Management ORGA+BILAN (6 axes organisation et suivi). Vous obtiendrez un diagnostic complet avec des recommandations personnalisées et un plan d'actions.",
  },
  {
    question: 'Comment est conçu le diagnostic ?',
    answer:
      "L'outil est basé sur le référentiel PREMS de l'OPPBTP, spécifiquement adapté pour les entreprises SCOP du BTP. Il comprend 44 questions réparties en deux questionnaires : le premier évalue les risques terrain via la méthode HALTES (5 axes), le second évalue le management via ORGA (4 axes) et le suivi via BILAN (2 axes). Les questions sont adaptées à votre métier.",
  },
  {
    question: "Combien de temps dure l'évaluation ?",
    answer:
      "L'évaluation complète dure entre 15 et 20 minutes. Vous pouvez la mettre en pause à tout moment et la reprendre plus tard grâce à la sauvegarde automatique.",
  },
  {
    question: "Que se passe-t-il après l'évaluation ?",
    answer:
      "À la fin de l'évaluation, vous obtenez un score détaillé par thème avec un radar visuel de vos résultats. Vous pouvez consulter le catalogue de plus de 50 solutions OPPBTP, construire votre plan d'actions personnalisé et générer votre attestation officielle.",
  },
  {
    question: 'Comment obtenir le badge doré ?',
    answer:
      "Le badge doré est réservé aux entreprises ayant obtenu le niveau 5 casques et disposant d'une certification MASE ou ISO 45001 en cours de validité. Déposez votre certificat lors de la demande d'attestation, et l'équipe OPPBTP validera votre dossier sous 5 jours ouvrés.",
  },
  {
    question: "L'outil est-il gratuit ?",
    answer:
      "Oui, l'outil est entièrement gratuit. Il est développé par l'OPPBTP et mis à disposition des entreprises SCOP du BTP. La connexion se fait via votre compte PréventionBTP (id.preventionbtp.fr).",
  },
]
